import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCloseOnClickOutside } from '../../hooks/useCloseOnClickOutside';
import { formatLocalDate } from '../../utils/dateUtils';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { sessionManager } from '../../config/sessionManager';
import { showErrorMsg } from '../../config/common';
import TeamsAnalyticsTable, { StatMatrixField } from '../../components/shared/TeamsAnalyticsTable';
import { filterGamesByMode, GAME_MODE_PRACTICE, parseGameMode } from '../../utils/seasonType';

// Ensure chart components are registered once to avoid runtime scale errors
const highlightCategoryPlugin = {
  id: 'highlightCategory',
  beforeDatasetsDraw(chart: any) {
    const tooltip = chart.tooltip;
    if (!tooltip || !tooltip.getActiveElements().length) return;
    const active = tooltip.getActiveElements()[0];
    const index = active.index;
    const xScale = chart.scales.x;
    if (!xScale?.getPixelForTick) return;
    const ctx = chart.ctx;
    const center = xScale.getPixelForTick(index);
    let width = 40;
    try {
      const next = xScale.getPixelForTick(index + 1);
      width = next - center;
    } catch {}
    try {
      const prev = xScale.getPixelForTick(index - 1);
      width = center - prev;
    } catch {}
    const startX = center - width / 2;
    const { top, bottom } = chart.chartArea;
    ctx.save();
    ctx.fillStyle = 'rgba(173,216,230,0.35)';
    ctx.fillRect(startX, top, width, bottom - top);
    ctx.restore();
  },
};

// Register Chart.js components once globally
if (!(ChartJS as any)._statdogRegistered) {
  ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend, highlightCategoryPlugin);
  (ChartJS as any)._statdogRegistered = true;
}

// Types
interface ReportSectionProps {
  title: string;
  children?: React.ReactNode;
}

// Reusable Components
const ReportSection: React.FC<ReportSectionProps> = React.memo(({ title, children }) => {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sd-dark text-2xl font-bold">{title}</p>
      {/* Mobile-only card look; outer provides scroll, inner hugs table width */}
      <div className="-mx-4 md:mx-0 overflow-x-auto md:overflow-visible">
        <div className="inline-block md:block md:w-full align-top rounded-none md:rounded-xl w-full">{children}</div>
      </div>
    </div>
  );
});

// Note: Using imported TeamsAnalyticsTable component which includes its own KPI and highlight color definitions

// Define base fields for each section
const basePossessionFields: StatMatrixField[] = [
  // Order aligned with Game Teams report
  { label: 'Possession Time', key: 'posTimeUs', themKey: 'posTimeThem' },
  {
    label: 'Possession %',
    key: 'percentUs',
    themKey: 'percentThem',
  },
  {
    label: 'Possession Count',
    key: 'countUs',
    themKey: 'countThem',
  },
  { label: 'Avg. Time/Poss.', key: 'us', themKey: 'them' },
  {
    label: 'Poss. Efficiency',
    key: 'efficiencyUs',
    themKey: 'efficiencyThem',
  },
  { label: 'Ground Balls', key: 'gbUs', themKey: 'gbThem' },
  {
    label: 'Caused Turnovers',
    key: 'ctUs',
    themKey: 'ctThem',
  },
  { label: 'Turnovers', key: 'toUs', themKey: 'toThem' },
];

const shotClockFields: StatMatrixField[] = [
  {
    label: 'Shot Clock Vs.',
    key: 'shotClockViolationsUs',
    themKey: 'shotClockViolationsThem',
  },
];
const offenseFields: StatMatrixField[] = [
  { label: 'Shots', key: 'shotsUs', themKey: 'shotsThem' },
  { label: 'Goals', key: 'goalsUs', themKey: 'goalsThem' },
  { label: 'Shot %', key: 'shotPctUs', themKey: 'shotPctThem' },
  { label: 'SOG%', key: 'sogPctUs', themKey: 'sogPctThem' },
  { label: 'MUOS', key: 'emosUs', themKey: 'emosThem' },
  { label: 'MUOF', key: 'emofUs', themKey: 'emofThem' },
  { label: 'MUOS %', key: 'emosPctUs', themKey: 'emosPctThem' },
];
const defenseFields: StatMatrixField[] = [
  {
    label: 'Shots Allowed',
    key: 'shotsAllowedUs',
    themKey: 'shotsAllowedThem',
  },
  { label: 'MDDS', key: 'mddsUs', themKey: 'mddsThem' },
  { label: 'MDDF', key: 'mddfUs', themKey: 'mddfThem' },
  { label: 'MDDS %', key: 'mddsPctUs', themKey: 'mddsPctThem' },
  { label: 'Blocked Shots', key: 'blockedUs', themKey: 'blockedThem' },
];
const goalieFields: StatMatrixField[] = [
  { label: 'Shots on Goal', key: 'shotsOnGoalUs', themKey: 'shotsOnGoalThem' },
  { label: 'Saves', key: 'savesUs', themKey: 'savesThem' },
  { label: 'Save %', key: 'savePctUs', themKey: 'savePctThem' },
];
const clearsRidesFields: StatMatrixField[] = [
  {
    label: 'Clear Attempts',
    key: 'clearAttemptsUs',
    themKey: 'clearAttemptsThem',
  },
  { label: 'Clears', key: 'clearsUs', themKey: 'clearsThem' },
  { label: 'Clear %', key: 'clearPctUs', themKey: 'clearPctThem' },
  { label: 'Ride Wins', key: 'rideWinsUs', themKey: 'rideWinsThem' },
];
const faceoffsFields: StatMatrixField[] = [
  { label: 'DC Wins', key: 'faceoffWinsUs', themKey: 'faceoffWinsThem' },
  {
    label: 'DC Losses',
    key: 'faceoffLossesUs',
    themKey: 'faceoffLossesThem',
  },
  { label: 'DC %', key: 'faceoffPctUs', themKey: 'faceoffPctThem' },
];
const eightMeterFields: StatMatrixField[] = [
  { label: 'Attempts', key: 'eightMeterAttemptsUs', themKey: 'eightMeterAttemptsThem' },
  { label: '8M Goals', key: 'eightMeterGoalsUs', themKey: 'eightMeterGoalsThem' },
  { label: '8M Conversion %', key: 'eightMeterPctUs', themKey: 'eightMeterPctThem' },
];
const foulsFields: StatMatrixField[] = [
  { label: 'Shooting Space', key: 'foulShootingSpaceUs', themKey: 'foulShootingSpaceThem' },
  { label: '3 Seconds', key: 'foulThreeSecondUs', themKey: 'foulThreeSecondThem' },
  { label: 'Dangerous Checks', key: 'foulDangerousCheckUs', themKey: 'foulDangerousCheckThem' },
  { label: 'Cards', key: 'cardsUs', themKey: 'cardsThem' },
];

// Note: Local StatMatrixTable removed - using imported TeamsAnalyticsTable component

// Helper functions for data transformation
function mapStatsToMatrixColumn(us: any, them: any) {
  const formatTime = (seconds?: number) => {
    if (typeof seconds !== 'number' || isNaN(seconds)) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // For percentages, use the provided percentage directly (don't divide by games)
  const formatPercent = (percent?: number) => {
    if (percent === undefined || percent === null || isNaN(percent)) return '-';
    return `${Math.round(percent)}%`;
  };

  return {
    // Possession Time (mm:ss)
    posTimeUs: us.possessionTime !== undefined ? formatTime(Number(us.possessionTime)) : '0:00',
    posTimeThem: them.possessionTime !== undefined ? formatTime(Number(them.possessionTime)) : '0:00',
    // Avg. Time/Poss. should display mm:ss
    us: us.avgTimePerPossession !== undefined ? formatTime(Number(us.avgTimePerPossession)) : '-',
    them: them.avgTimePerPossession !== undefined ? formatTime(Number(them.avgTimePerPossession)) : '-',
    percentUs: formatPercent(us.possessionPercent),
    percentThem: formatPercent(them.possessionPercent),
    countUs: us.possessionCount ?? '-',
    countThem: them.possessionCount ?? '-',
    gbUs: us.groundballs ?? '-',
    gbThem: them.groundballs ?? '-',
    ctUs: us.causedturnover ?? '-',
    ctThem: them.causedturnover ?? '-',
    toUs: us.turnovers ?? '-',
    toThem: them.turnovers ?? '-',
    efficiencyUs: formatPercent(us.possessionEfficiency),
    efficiencyThem: formatPercent(them.possessionEfficiency),
    shotClockViolationsUs: us.shotClockViolations ?? '-',
    shotClockViolationsThem: them.shotClockViolations ?? '-',
    shotsUs: us.shots ?? '-',
    shotsThem: them.shots ?? '-',
    goalsUs: us.goals ?? '-',
    goalsThem: them.goals ?? '-',
    shotPctUs: formatPercent(us.shotPercent),
    shotPctThem: formatPercent(them.shotPercent),
    sogPctUs: formatPercent(us.sogPercent),
    sogPctThem: formatPercent(them.sogPercent),
    emosUs: us.emos ?? '-',
    emosThem: them.emos ?? '-',
    emofUs: us.emof ?? '-',
    emofThem: them.emof ?? '-',
    emosPctUs: formatPercent(us.emosPercent),
    emosPctThem: formatPercent(them.emosPercent),
    shotsAllowedUs: us.shotsallowed ?? '-',
    shotsAllowedThem: them.shotsallowed ?? '-',
    mddsUs: us.mdds ?? '-',
    mddsThem: them.mdds ?? '-',
    mddfUs: us.mddf ?? '-',
    mddfThem: them.mddf ?? '-',
    mddsPctUs: formatPercent(us.mddsPercent),
    mddsPctThem: formatPercent(them.mddsPercent),
    blockedUs: us.blocked ?? '-',
    blockedThem: them.blocked ?? '-',
    shotsOnGoalUs: us.shotsongoal ?? '-',
    shotsOnGoalThem: them.shotsongoal ?? '-',
    savesUs: us.saved ?? '-',
    savesThem: them.saved ?? '-',
    savePctUs: formatPercent(us.savePercent),
    savePctThem: formatPercent(them.savePercent),
    clearAttemptsUs: us.clearattempts ?? '-',
    clearAttemptsThem: them.clearattempts ?? '-',
    clearsUs: us.clears ?? '-',
    clearsThem: them.clears ?? '-',
    clearPctUs: formatPercent(us.clearPercent),
    clearPctThem: formatPercent(them.clearPercent),
    rideWinsUs: us.ridewins ?? '-',
    rideWinsThem: them.ridewins ?? '-',
    faceoffWinsUs: us.faceoffwins ?? '-',
    faceoffWinsThem: them.faceoffwins ?? '-',
    faceoffLossesUs: us.faceofflosses ?? '-',
    faceoffLossesThem: them.faceofflosses ?? '-',
    faceoffPctUs: formatPercent(us.faceoffPercent),
    faceoffPctThem: formatPercent(them.faceoffPercent),
    eightMeterAttemptsUs: us.eightMeterAttempts ?? 0,
    eightMeterAttemptsThem: them.eightMeterAttempts ?? 0,
    eightMeterGoalsUs: us.eightMeterGoals ?? 0,
    eightMeterGoalsThem: them.eightMeterGoals ?? 0,
    eightMeterPctUs: formatPercent(us.eightMeterPercent ?? 0),
    eightMeterPctThem: formatPercent(them.eightMeterPercent ?? 0),
    // 8M fouls are logged on the side awarded the 8M, so our fouls come from their 8M fields
    foulShootingSpaceUs: them.eightMeterShootingSpace ?? 0,
    foulShootingSpaceThem: us.eightMeterShootingSpace ?? 0,
    foulThreeSecondUs: them.eightMeterThreeSecond ?? 0,
    foulThreeSecondThem: us.eightMeterThreeSecond ?? 0,
    foulDangerousCheckUs: them.eightMeterDangerousCheck ?? 0,
    foulDangerousCheckThem: us.eightMeterDangerousCheck ?? 0,
    cardsUs: us.cards ?? 0,
    cardsThem: them.cards ?? 0,
  };
}

function shouldIncludePerGameColumn(selectedGameId: string, selectedCustomGameIds: string[]): boolean {
  if (selectedGameId === 'custom') {
    return selectedCustomGameIds.length > 1;
  }
  if (!selectedGameId || selectedGameId === 'all' || selectedGameId === 'league') {
    return true;
  }
  return false;
}

function appendSeasonGameFilterToUrl(
  url: string,
  selectedGameId: string,
  selectedCustomGameIds: string[],
  gameMode?: string,
): string {
  let next = url;
  if (selectedGameId === 'league') {
    next = `${next}&league=true`;
  } else if (selectedGameId === 'custom') {
    if (selectedCustomGameIds.length === 1) {
      next = `${next}&gameId=${selectedCustomGameIds[0]}`;
    } else {
      next = `${next}&gameIds=${encodeURIComponent(selectedCustomGameIds.join(','))}`;
    }
  } else if (selectedGameId && selectedGameId !== 'all') {
    next = `${next}&gameId=${selectedGameId}`;
  }
  if (gameMode) {
    next = `${next}&gameMode=${encodeURIComponent(gameMode)}`;
  }
  return next;
}

function areStringArraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function transformStatSeriesToColumns(statSeriesData: any, includePerGameColumn: boolean) {
  const { periods, stats, perGame } = statSeriesData;
  const columns: any[] = [];

  // Transform each period's stats
  periods.forEach((period: string) => {
    const periodStats = stats[period];
    if (periodStats) {
      columns.push({
        label: period,
        ...mapStatsToMatrixColumn(periodStats.us, periodStats.them),
      });
    }
  });

  // Per Game averages only apply when reviewing multiple games
  if (perGame && includePerGameColumn) {
    const perGameColumn = {
      label: 'Per Game',
      ...mapStatsToMatrixColumn(perGame.us, perGame.them),
    };
    columns.push(perGameColumn);
  }

  return columns;
}

const TEAMS_FILTER_TRIGGER_CLASS =
  'bg-white w-full box-border h-8 min-h-8 max-h-8 text-sd-navy font-semibold px-2 py-0 rounded-md border border-sd-navy shadow-sm focus:outline-none appearance-none pr-6 text-sm leading-none';

// Main Component

const SeasonTeamsPage: React.FC = React.memo(() => {
  const clientId = sessionManager.getClientId();
  const seasonId = sessionManager.getSeasonId();
  const gamesMode = parseGameMode(sessionManager.getGamesMode());

  // State for games, opponents, and selections
  const [games, setGames] = useState<any[]>([]);
  const [selectedGameId, setSelectedGameId] = useState<string>('all');
  const [selectedCustomGameIds, setSelectedCustomGameIds] = useState<string[]>([]);
  const [gameFilterReady, setGameFilterReady] = useState<boolean>(false);
  const [isGameDropdownOpen, setIsGameDropdownOpen] = useState<boolean>(false);
  const gameDropdownRef = useRef<HTMLDivElement>(null);
  // New: show side (both/us/them) and selected stat for per-game chart
  const [showSide, setShowSide] = useState<'both' | 'us' | 'them' | 'wins' | 'losses'>('both');
  const [selectedStat, setSelectedStat] = useState<string>('Shot %');
  const [periods, setPeriods] = useState<string[]>([]);
  const [columns, setColumns] = useState<any[]>([]);
  const [shotClockEnabled, setShotClockEnabled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const isChartLoadingRef = useRef<boolean>(false);
  // Season KPI targets
  const [kpiTargets, setKpiTargets] = useState<any | null>(null);
  const [gameCount, setGameCount] = useState<number>(1); // Get from API instead of calculating manually

  // KPI helpers
  type KPIColor = 'green' | 'yellow' | 'orange' | 'red' | 'none';
  const getKpiColor = useCallback(
    (current: number | string, target: number | string, isInverse: boolean = false): KPIColor => {
      // Coerce to numbers first to handle cases where targets come back as strings (e.g. "0")
      const curNum = Number(current);
      const tgtNum = Number(target);
      // If target is 0/NaN/negative we suppress coloring entirely
      if (!isFinite(curNum) || !isFinite(tgtNum) || tgtNum === 0) return 'none';
      // Compute ratio respecting inverse metrics (where lower is better)
      const ratio = isInverse ? tgtNum / curNum : curNum / tgtNum;
      if (!isFinite(ratio)) return 'none'; // guard divide-by-zero edge
      if (ratio >= 1.0) return 'green';
      if (ratio >= 0.85) return 'yellow';
      if (ratio >= 0.8) return 'orange';
      return 'red';
    },
    [],
  );

  // Helper to parse numbers from strings like "45%" or "12.3s"
  const parseNum = useCallback((v: any): number => {
    if (v === undefined || v === null || v === '-') return NaN;
    const s = String(v).replace(/%|s/g, '').trim();
    const n = Number(s);
    return isNaN(n) ? NaN : n;
  }, []);

  // Annotate Per Game column with Us KPI colors
  const applyKpiColorsToColumns = useCallback(
    (cols: any[], targets: any | null) => {
      if (!targets) return cols;
      const copy = cols.map((c) => ({ ...c }));
      const labelsToColor = new Set(['Q1', 'Q2', 'Q3', 'Q4', 'H1', 'H2', 'OT', 'Totals', 'Per Game']);

      // Calculate actual periods per game based on available periods
      // Exclude "Totals" and "Per Game" from the count
      const actualPeriodsPerGame = periods.filter((p) => p !== 'Totals' && p !== 'Per Game').length || 4; // Default to 4 if no periods info available

      for (const col of copy) {
        if (!labelsToColor.has(col.label)) continue;
        try {
          // Determine scaling factors based on column type
          const isPerGame = col.label === 'Per Game';
          const isTotals = col.label === 'Totals';

          // Possession
          if (targets.possession) {
            // Possession % is always a percentage - no scaling needed
            const p = parseNum(col.percentUs);
            if (isFinite(p)) {
              col['percentUsKpiColor'] = getKpiColor(p, targets.possession.possessionPercent);
              col['percentUsKpiTarget'] = `${targets.possession.possessionPercent}%`;
            }

            // Ground balls % calculation
            const usgb = parseNum(col.gbUs);
            const themgb = parseNum(col.gbThem);
            if (isFinite(usgb) && isFinite(themgb) && usgb + themgb > 0) {
              col['gbUsKpiColor'] = getKpiColor((usgb / (usgb + themgb)) * 100, targets.possession.groundBallsPP);
              col['gbUsKpiTarget'] = `${targets.possession.groundBallsPP}%`;
            }

            // Turnovers - scale based on column type
            const to = parseNum(col.toUs);
            if (isFinite(to)) {
              let turnoverTarget = targets.possession.maxTurnoversPP;
              if (isPerGame) {
                turnoverTarget = targets.possession.maxTurnoversPP * actualPeriodsPerGame; // Use actual periods including OT
              } else if (isTotals) {
                // For totals: per-period target * periods per game * number of games
                turnoverTarget = targets.possession.maxTurnoversPP * actualPeriodsPerGame * gameCount;
              } else {
                // For individual periods (Q1, Q2, etc.): per-period target * number of games
                // These represent totals for that period across all games
                turnoverTarget = targets.possession.maxTurnoversPP * gameCount;
              }
              col['toUsKpiColor'] = getKpiColor(to, turnoverTarget, true);
              col['toUsKpiTarget'] = turnoverTarget.toString();
            }
          }

          // Offense
          if (targets.offense) {
            // Shots - scale based on column type
            const shots = parseNum(col.shotsUs);
            if (isFinite(shots)) {
              let shotsTarget = targets.offense.minShotsPP;
              if (isPerGame) {
                shotsTarget = targets.offense.minShotsPP * actualPeriodsPerGame;
              } else if (isTotals) {
                // For totals: per-period target * periods per game * number of games
                shotsTarget = targets.offense.minShotsPP * actualPeriodsPerGame * gameCount;
              } else {
                // For individual periods (Q1, Q2, etc.): per-period target * number of games
                // These represent totals for that period across all games
                shotsTarget = targets.offense.minShotsPP * gameCount;
              }
              col['shotsUsKpiColor'] = getKpiColor(shots, shotsTarget);
              col['shotsUsKpiTarget'] = shotsTarget.toString();
            }

            // Goals - scale based on column type
            const goals = parseNum(col.goalsUs);
            if (isFinite(goals)) {
              let goalsTarget = targets.offense.minGoalsPP;
              if (isPerGame) {
                goalsTarget = targets.offense.minGoalsPP * actualPeriodsPerGame;
              } else if (isTotals) {
                // For totals: per-period target * periods per game * number of games
                goalsTarget = targets.offense.minGoalsPP * actualPeriodsPerGame * gameCount;
              } else {
                // For individual periods (Q1, Q2, etc.): per-period target * number of games
                // These represent totals for that period across all games
                goalsTarget = targets.offense.minGoalsPP * gameCount;
              }
              col['goalsUsKpiColor'] = getKpiColor(goals, goalsTarget);
              col['goalsUsKpiTarget'] = goalsTarget.toString();
            }

            // Shot % is always a percentage - no scaling needed
            const shotPct = parseNum(col.shotPctUs);
            if (isFinite(shotPct)) {
              col['shotPctUsKpiColor'] = getKpiColor(shotPct, targets.offense.shotPercent);
              col['shotPctUsKpiTarget'] = `${targets.offense.shotPercent}%`;
            }

            // MUOS % is always a percentage - no scaling needed
            const emosPct = parseNum(col.emosPctUs);
            if (isFinite(emosPct)) {
              col['emosPctUsKpiColor'] = getKpiColor(emosPct, targets.offense.emosPercent);
              col['emosPctUsKpiTarget'] = `${targets.offense.emosPercent}%`;
            }
          }

          // Defense
          if (targets.defense) {
            // Shots Allowed - scale based on column type
            const shotsAllowed = parseNum(col.shotsAllowedUs);
            if (isFinite(shotsAllowed)) {
              let shotsAllowedTarget = targets.defense.maxShotsAPP;
              if (isPerGame) {
                shotsAllowedTarget = targets.defense.maxShotsAPP * actualPeriodsPerGame;
              } else if (isTotals) {
                // For totals: per-period target * periods per game * number of games
                shotsAllowedTarget = targets.defense.maxShotsAPP * actualPeriodsPerGame * gameCount;
              } else {
                // For individual periods (Q1, Q2, etc.): per-period target * number of games
                // These represent totals for that period across all games
                shotsAllowedTarget = targets.defense.maxShotsAPP * gameCount;
              }
              col['shotsAllowedUsKpiColor'] = getKpiColor(shotsAllowed, shotsAllowedTarget, true);
              col['shotsAllowedUsKpiTarget'] = shotsAllowedTarget.toString();
            }

            // MDDS % is always a percentage - no scaling needed
            const mddsPct = parseNum(col.mddsPctUs);
            if (isFinite(mddsPct)) {
              col['mddsPctUsKpiColor'] = getKpiColor(mddsPct, targets.defense.mddsPercent);
              col['mddsPctUsKpiTarget'] = `${targets.defense.mddsPercent}%`;
            }

            // Save % is always a percentage - no scaling needed
            const savePct = parseNum(col.savePctUs);
            if (isFinite(savePct)) {
              col['savePctUsKpiColor'] = getKpiColor(savePct, targets.defense.savePercent);
              col['savePctUsKpiTarget'] = `${targets.defense.savePercent}%`;
            }
          }

          // Clears / Rides
          if (targets.clearsRides) {
            // Clear % is always a percentage - no scaling needed
            const clearPct = parseNum(col.clearPctUs);
            if (isFinite(clearPct)) {
              col['clearPctUsKpiColor'] = getKpiColor(clearPct, targets.clearsRides.clearPercent);
              col['clearPctUsKpiTarget'] = `${targets.clearsRides.clearPercent}%`;
            }

            // Ride Wins - scale based on column type
            const rideWins = parseNum(col.rideWinsUs);
            if (isFinite(rideWins)) {
              let rideWinsTarget = targets.clearsRides.minRidesPP;
              if (isPerGame) {
                rideWinsTarget = targets.clearsRides.minRidesPP * actualPeriodsPerGame;
              } else if (isTotals) {
                // For totals: per-period target * periods per game * number of games
                rideWinsTarget = targets.clearsRides.minRidesPP * actualPeriodsPerGame * gameCount;
              } else {
                // For individual periods (Q1, Q2, etc.): per-period target * number of games
                // These represent totals for that period across all games
                rideWinsTarget = targets.clearsRides.minRidesPP * gameCount;
              }
              col['rideWinsUsKpiColor'] = getKpiColor(rideWins, rideWinsTarget);
              col['rideWinsUsKpiTarget'] = rideWinsTarget.toString();
            }
          }

          // Draw Controls
          if (targets.faceoffs) {
            // DC % is always a percentage - no scaling needed
            const facePct = parseNum(col.faceoffPctUs);
            if (isFinite(facePct)) {
              col['faceoffPctUsKpiColor'] = getKpiColor(facePct, targets.faceoffs.faceoffPercent);
              col['faceoffPctUsKpiTarget'] = `${targets.faceoffs.faceoffPercent}%`;
            }
          }
        } catch {}
      }
      return copy;
    },
    [getKpiColor, parseNum, periods, gameCount],
  );

  // Mobile chart horizontal scroll state
  const chartScrollRef = useRef<HTMLDivElement>(null);
  const [showChartLeft, setShowChartLeft] = useState(false);
  const [showChartRight, setShowChartRight] = useState(false);
  // statSeries.labels can contain multi-line labels: [opponentAbbrev, date]; also store fullOpponents for tooltip
  const [statSeries, setStatSeries] = useState<{
    labels: (string | string[])[];
    us: number[];
    them: number[];
    fullOpponents: string[];
    outcomes: ('win' | 'loss' | 'tie' | null)[];
  }>({ labels: [], us: [], them: [], fullOpponents: [], outcomes: [] });

  // Cache for all games chart data - fetched once per game selection change
  const [allGamesChartData, setAllGamesChartData] = useState<{
    games: any[];
    gameStats: any;
  } | null>(null);
  const selectableGames = useMemo(() => filterGamesByMode(games, gamesMode), [games, gamesMode]);

  // Generate dynamic possession fields based on shot clock enabled state
  const getPossessionFields = (): StatMatrixField[] => {
    const fields = [...basePossessionFields];
    if (shotClockEnabled) {
      fields.push(...shotClockFields);
    }
    return fields;
  };

  // Fetch stats for all periods and set columns
  const fetchAllPeriodsStats = useCallback(async () => {
    if (!clientId || !seasonId) return;
    if (selectedGameId === 'custom' && selectedCustomGameIds.length === 0) {
      setColumns([]);
      setGameCount(0);
      return;
    }

    setIsLoading(true);
    try {
      // Single API call to get all period stats
      let url = `${API_URLS.GET_SEASON_STAT_SERIES}?clientId=${clientId}&seasonId=${seasonId}`;
      url = appendSeasonGameFilterToUrl(url, selectedGameId, selectedCustomGameIds, gamesMode);
      const includePerGameColumn = shouldIncludePerGameColumn(selectedGameId, selectedCustomGameIds);

      const response = await api.get(url);
      if (response.data.success) {
        const { data } = response.data;

        // Set shot clock enabled from metadata
        if (data.metadata && typeof data.metadata.shotClock !== 'undefined') {
          setShotClockEnabled(!!data.metadata.shotClock);
        }

        // Extract gameCount for KPI scaling
        setGameCount(Number.isFinite(data.gameCount) ? data.gameCount : 0);

        // Track period labels for KPI scaling (Per Game only when aggregating multiple games)
        let nextPeriods = [...data.periods];
        if (includePerGameColumn && !nextPeriods.includes('Per Game')) {
          nextPeriods.push('Per Game');
        } else if (!includePerGameColumn) {
          nextPeriods = nextPeriods.filter((period: string) => period !== 'Per Game');
        }
        setPeriods((prev) => (areStringArraysEqual(prev, nextPeriods) ? prev : nextPeriods));

        // Transform the data to frontend format
        const transformedColumns = transformStatSeriesToColumns(data, includePerGameColumn);
        // Apply KPI colors if targets are already available
        const columnsWithKpi = kpiTargets
          ? applyKpiColorsToColumns(transformedColumns, kpiTargets)
          : transformedColumns;
        setColumns(columnsWithKpi);
      }
    } catch (error) {
      console.error(`[SeasonStatSeries] Error fetching stat series:`, error);
    } finally {
      setIsLoading(false);
    }
  }, [clientId, seasonId, selectedGameId, selectedCustomGameIds, kpiTargets, applyKpiColorsToColumns, gamesMode]);

  // Mapping of selectable stats to API keys (stable reference)
  const statOptions = useMemo(
    () => [
      {
        label: 'Possession %',
        usKey: 'possessionPercent',
        themKey: 'possessionPercent',
        percent: true,
      },
      {
        label: 'Possession Count',
        usKey: 'possessionCount',
        themKey: 'possessionCount',
      },
      {
        label: 'Poss. Efficiency',
        usKey: 'possessionEfficiency',
        themKey: 'possessionEfficiency',
        percent: true,
      },
      { label: 'Ground Balls', usKey: 'groundballs', themKey: 'groundballs' },
      {
        label: 'Caused Turnovers',
        usKey: 'causedturnover',
        themKey: 'causedturnover',
      },
      { label: 'Turnovers', usKey: 'turnovers', themKey: 'turnovers' },
      {
        label: 'Shot Clock Violations',
        usKey: 'shotClockViolations',
        themKey: 'shotClockViolations',
      },
      { label: 'Shots', usKey: 'shots', themKey: 'shots' },
      { label: 'Goals', usKey: 'goals', themKey: 'goals' },
      {
        label: 'Shot %',
        usKey: 'shotPercent',
        themKey: 'shotPercent',
        percent: true,
      },
      {
        label: 'SOG%',
        usKey: 'sogPercent',
        themKey: 'sogPercent',
        percent: true,
      },
      { label: 'MUOS Wins', usKey: 'emos', themKey: 'emos' },
      { label: 'MUOF', usKey: 'emof', themKey: 'emof' },
      {
        label: 'MUOS %',
        usKey: 'emosPercent',
        themKey: 'emosPercent',
        percent: true,
      },
      {
        label: 'Shots Allowed',
        usKey: 'shotsallowed',
        themKey: 'shotsallowed',
      },
      { label: 'MDDS', usKey: 'mdds', themKey: 'mdds' },
      { label: 'MDDF', usKey: 'mddf', themKey: 'mddf' },
      {
        label: 'MDDS %',
        usKey: 'mddsPercent',
        themKey: 'mddsPercent',
        percent: true,
      },
      { label: 'Blocked', usKey: 'blocked', themKey: 'blocked' },
      { label: 'Shots on Goal', usKey: 'shotsongoal', themKey: 'shotsongoal' },
      { label: 'Saves', usKey: 'saves', themKey: 'saves' },
      {
        label: 'Save %',
        usKey: 'savePercent',
        themKey: 'savePercent',
        percent: true,
      },
      {
        label: 'Clear Attempts',
        usKey: 'clearattempts',
        themKey: 'clearattempts',
      },
      { label: 'Clears', usKey: 'clears', themKey: 'clears' },
      {
        label: 'Clear %',
        usKey: 'clearPercent',
        themKey: 'clearPercent',
        percent: true,
      },
      { label: 'Ride Wins', usKey: 'ridewins', themKey: 'ridewins' },
      { label: 'DC Wins', usKey: 'faceoffwins', themKey: 'faceoffwins' },
      {
        label: 'DC Losses',
        usKey: 'faceofflosses',
        themKey: 'faceofflosses',
      },
      {
        label: 'DC %',
        usKey: 'faceoffPercent',
        themKey: 'faceoffPercent',
        percent: true,
      },
      { label: '8M Attempts', usKey: 'eightMeterAttempts', themKey: 'eightMeterAttempts' },
      { label: '8M Goals', usKey: 'eightMeterGoals', themKey: 'eightMeterGoals' },
      {
        label: '8M Conversion %',
        usKey: 'eightMeterPercent',
        themKey: 'eightMeterPercent',
        percent: true,
      },
    ],
    [],
  );

  // Fetch all games chart data once when game selection changes
  const fetchAllGamesChartData = useCallback(async () => {
    if (!clientId || !seasonId || isChartLoadingRef.current) return;
    if (selectedGameId === 'custom' && selectedCustomGameIds.length === 0) {
      setAllGamesChartData({ games: [], gameStats: {} });
      return;
    }

    isChartLoadingRef.current = true;
    try {
      let url = `${API_URLS.GET_SEASON_CHART_SERIES}?clientId=${clientId}&seasonId=${seasonId}`;
      url = appendSeasonGameFilterToUrl(url, selectedGameId, selectedCustomGameIds, gamesMode);

      const response = await api.get(url);
      if (response.data.success) {
        setAllGamesChartData(response.data.data);
      } else {
        setAllGamesChartData(null);
      }
    } catch (error) {
      setAllGamesChartData(null);
    } finally {
      isChartLoadingRef.current = false;
    }
  }, [clientId, seasonId, selectedGameId, selectedCustomGameIds, gamesMode]);

  const fetchStatSeries = useCallback(() => {
    const option = statOptions.find((o) => o.label === selectedStat);
    if (!option || !allGamesChartData) return;

    const { games, gameStats } = allGamesChartData;
    const labels: (string | string[])[] = [];
    const usVals: number[] = [];
    const themVals: number[] = [];
    const outcomes: ('win' | 'loss' | 'tie' | null)[] = [];

    // Process games using cached data
    const processGame = (g: any) => {
      const gameId = g._id.toString();
      const stats = gameStats[gameId];

      if (stats) {
        const { us, them } = stats;
        const usValRaw = us?.[option.usKey];
        const themValRaw = them?.[option.themKey];

        // Use outcome from backend calculation
        outcomes.push(g.outcome || null);

        const formatVal = (v: any) => {
          if (v === null || v === undefined || v === '-') return 0;
          const num = Number(v);
          if (isNaN(num)) return 0;
          return option.percent ? Math.round(num) : num;
        };

        usVals.push(formatVal(usValRaw));
        themVals.push(formatVal(themValRaw));
      } else {
        usVals.push(0);
        themVals.push(0);
        outcomes.push(null);
      }

      const dateStr = g.date
        ? formatLocalDate(g.date, undefined, {
            month: 'numeric',
            day: 'numeric',
          })
        : '';
      const oppFull = (g.opponentName || '').trim();
      // Use multi-line label: opponent on top, date below
      labels.push([oppFull || 'Opponent', dateStr]);
    };

    // Process games based on selection
    if (selectedGameId === 'custom') {
      const selectedSet = new Set(selectedCustomGameIds);
      games.filter((g) => selectedSet.has(String(g._id))).forEach(processGame);
    } else if (selectedGameId && selectedGameId !== 'all' && selectedGameId !== 'league') {
      const game = games.find((g) => g._id === selectedGameId);
      if (game) {
        processGame(game);
      }
    } else {
      games.forEach(processGame);
    }

    setStatSeries({
      labels,
      us: usVals,
      them: themVals,
      fullOpponents: labels.map((l) => (Array.isArray(l) ? (l[0] as string) : String(l))),
      outcomes,
    });
  }, [selectedStat, allGamesChartData, selectedGameId, selectedCustomGameIds, statOptions]);

  // Fetch Season KPI targets
  const fetchSeasonKpiTargets = useCallback(async () => {
    try {
      const url = `${API_URLS.GET_SEASON_KPI_TARGETS}?clientId=${clientId}&seasonId=${seasonId}`;
      const res = await api.get(url);
      if (res.data?.success) {
        setKpiTargets(res.data.data);
      }
    } catch (e) {
      console.error('Failed to fetch season KPI targets', e);
    } finally {
      // no-op
    }
  }, [clientId, seasonId]);

  const getSeasonGames = useCallback(async () => {
    try {
      const response = await api.get(`${API_URLS.GET_GAMES_ALL}?clientId=${clientId}&seasonId=${seasonId}`);
      if (response.data.success) {
        setGames(response.data.data || []);
      } else {
        showErrorMsg(response.data.message || 'Failed to fetch game information');
      }
    } catch (err: any) {
      showErrorMsg(err.response?.data?.message || err.message || 'Failed to fetch game information');
    }
  }, [clientId, seasonId]);

  useEffect(() => {
    getSeasonGames();
  }, [getSeasonGames]);

  // Check for session gameId after games are loaded and set initial selection
  useEffect(() => {
    if (games.length > 0) {
      const savedFilter = sessionManager.getSeasonGameFilter();
      if (savedFilter?.mode === 'league') {
        setSelectedGameId('league');
        setSelectedCustomGameIds([]);
      } else if (savedFilter?.mode === 'custom') {
        setSelectedGameId('custom');
        setSelectedCustomGameIds(savedFilter.customGameIds || []);
      } else if (savedFilter?.mode === 'single' && savedFilter.gameId && games.find((g) => String(g._id) === String(savedFilter.gameId))) {
        setSelectedGameId(savedFilter.gameId);
        setSelectedCustomGameIds([]);
      } else {
        setSelectedGameId('all');
        setSelectedCustomGameIds([]);
      }
      setGameFilterReady(true);
    }
  }, [games]);

  useEffect(() => {
    if (games.length === 0) return;
    setSelectedCustomGameIds((prev) => prev.filter((id) => selectableGames.some((g) => String(g._id) === String(id))));
  }, [games.length, selectableGames]);

  useEffect(() => {
    if (gamesMode === GAME_MODE_PRACTICE && selectedGameId === 'league') {
      setSelectedGameId('all');
      setSelectedCustomGameIds([]);
    }
  }, [gamesMode, selectedGameId]);

  useEffect(() => {
    if (!gameFilterReady) return;
    if (selectedGameId === 'league') {
      sessionManager.setSeasonGameFilter({ mode: 'league' });
      return;
    }
    if (selectedGameId === 'custom') {
      sessionManager.setSeasonGameFilter({ mode: 'custom', customGameIds: selectedCustomGameIds });
      return;
    }
    if (selectedGameId === 'all') {
      sessionManager.setSeasonGameFilter({ mode: 'all' });
      return;
    }
    if (selectedGameId) {
      sessionManager.setSeasonGameFilter({ mode: 'single', gameId: selectedGameId });
    }
  }, [gameFilterReady, selectedGameId, selectedCustomGameIds]);

  const formatGameOptionLabel = useCallback((game: any) => {
    const dateStr = game.date
      ? formatLocalDate(game.date, undefined, {
          month: 'numeric',
          day: 'numeric',
        })
      : '';
    const opp = game.opponentName || game.opponent || 'Game';
    return `${dateStr} ${opp}`.trim();
  }, []);

  const selectedGameLabel = useMemo(() => {
    if (selectedCustomGameIds.length > 1) return `${selectedCustomGameIds.length} Games Selected`;
    if (selectedCustomGameIds.length === 1) {
      const game = selectableGames.find((g) => String(g._id) === selectedCustomGameIds[0]);
      return game ? formatGameOptionLabel(game) : '1 Game Selected';
    }
    if (selectedGameId === 'league') return 'League Games';
    if (selectedGameId === 'all') return 'All Games';
    const game = selectableGames.find((g) => String(g._id) === selectedGameId);
    return game ? formatGameOptionLabel(game) : 'All Games';
  }, [formatGameOptionLabel, selectableGames, selectedCustomGameIds, selectedGameId]);

  const selectGamePreset = useCallback((value: 'all' | 'league') => {
    setSelectedGameId(value);
    setSelectedCustomGameIds([]);
    setIsGameDropdownOpen(false);
    if (value === 'league') {
      sessionManager.setGameId('league');
      sessionManager.setGameName('League Games');
    } else {
      sessionManager.setGameId('');
      sessionManager.setGameName('');
    }
  }, []);

  const toggleSelectedGame = useCallback((game: any) => {
    const gameId = String(game._id);
    setSelectedGameId('custom');
    sessionManager.setGameId('');
    sessionManager.setGameName('Selected Games');
    setSelectedCustomGameIds((prev) =>
      prev.includes(gameId) ? prev.filter((id) => id !== gameId) : [...prev, gameId],
    );
  }, []);

  // Fetch season KPI targets on mount or when ids change
  useEffect(() => {
    if (clientId && seasonId) {
      fetchSeasonKpiTargets();
    }
  }, [clientId, seasonId, fetchSeasonKpiTargets]);

  const closeGameDropdown = useCallback(() => setIsGameDropdownOpen(false), []);
  useCloseOnClickOutside(gameDropdownRef, isGameDropdownOpen, closeGameDropdown);

  useEffect(() => {
    fetchAllPeriodsStats();
  }, [fetchAllPeriodsStats]);

  // Fetch chart data only when game selection changes (not on display changes)
  useEffect(() => {
    fetchAllGamesChartData();
  }, [fetchAllGamesChartData]);

  // Update stat series when chart data changes or display settings change
  useEffect(() => {
    if (allGamesChartData) {
      fetchStatSeries();
    }
  }, [allGamesChartData, selectedStat, selectedGameId, fetchStatSeries]);

  // Re-apply KPI colors when targets load/update
  useEffect(() => {
    if (kpiTargets && columns.length > 0) {
      const updatedColumns = applyKpiColorsToColumns(columns, kpiTargets);
      if (JSON.stringify(updatedColumns) !== JSON.stringify(columns)) {
        setColumns(updatedColumns);
      }
    }
  }, [kpiTargets, columns, applyKpiColorsToColumns]);

  // Recalculate chart scroll button visibility on resize or data change
  useEffect(() => {
    const el = chartScrollRef.current;
    const recalc = () => {
      if (!el) return;
      setShowChartLeft(el.scrollLeft > 5);
      setShowChartRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 5);
    };
    recalc();
    el?.addEventListener('scroll', recalc, { passive: true } as any);
    window.addEventListener('resize', recalc);
    return () => {
      el?.removeEventListener('scroll', recalc as any);
      window.removeEventListener('resize', recalc);
    };
  }, [statSeries.labels]);

  const scrollChart = useCallback((dir: number) => {
    const el = chartScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * (el.clientWidth * 0.7), behavior: 'smooth' });
  }, []);

  // Optimize data decimation for large datasets
  const decimateData = useCallback((data: any[], labels: any[], maxPoints: number = 50) => {
    if (data.length <= maxPoints) return { data, labels };

    const step = Math.ceil(data.length / maxPoints);
    const decimatedData = [];
    const decimatedLabels = [];

    for (let i = 0; i < data.length; i += step) {
      decimatedData.push(data[i]);
      decimatedLabels.push(labels[i]);
    }

    return { data: decimatedData, labels: decimatedLabels };
  }, []);

  // Memoize chart data to prevent unnecessary re-renders with decimation
  const chartData = useMemo(() => {
    // Apply decimation if dataset is large (more than 50 points)
    const needsDecimation = statSeries.labels.length > 50;
    const maxPoints = 50;

    if (showSide === 'wins' || showSide === 'losses') {
      const values: number[] = [];
      const colors: (string | CanvasGradient | CanvasPattern)[] = [];
      const outcomes: ('win' | 'loss' | 'tie' | null)[] = [];

      for (let i = 0; i < statSeries.labels.length; i++) {
        const outcome = statSeries.outcomes[i];
        outcomes.push(outcome);

        if (showSide === 'wins') {
          if (outcome === 'win') {
            values.push(statSeries.us[i]);
            colors.push('#7fe526');
          } else if (outcome === 'loss') {
            values.push(statSeries.them[i]);
            colors.push('#255393');
          } else {
            values.push(NaN);
            colors.push('#cccccc');
          }
        } else {
          if (outcome === 'win') {
            values.push(statSeries.them[i]);
            colors.push('#255393');
          } else if (outcome === 'loss') {
            values.push(statSeries.us[i]);
            colors.push('#7fe526');
          } else {
            values.push(NaN);
            colors.push('#cccccc');
          }
        }
      }

      if (needsDecimation) {
        const { data: decimatedValues, labels: decimatedLabels } = decimateData(values, statSeries.labels, maxPoints);
        const { data: decimatedColors } = decimateData(colors, statSeries.labels, maxPoints);

        return {
          labels: decimatedLabels,
          datasets: [
            {
              label: showSide === 'wins' ? 'Winner' : 'Loser',
              data: decimatedValues,
              backgroundColor: decimatedColors,
              borderRadius: 6,
              maxBarThickness: 36,
              categoryPercentage: 0.35,
              barPercentage: 1.0,
            },
          ],
        };
      }

      return {
        labels: statSeries.labels,
        datasets: [
          {
            label: showSide === 'wins' ? 'Winner' : 'Loser',
            data: values,
            backgroundColor: colors,
            borderRadius: 6,
            maxBarThickness: 36,
            categoryPercentage: 0.35,
            barPercentage: 1.0,
          },
        ],
      };
    } else {
      // default: both/us/them
      if (needsDecimation) {
        const { data: decimatedUs, labels: decimatedLabels } = decimateData(
          statSeries.us,
          statSeries.labels,
          maxPoints,
        );
        const { data: decimatedThem } = decimateData(statSeries.them, statSeries.labels, maxPoints);

        const datasets = [];
        if (showSide !== 'them') {
          datasets.push({
            label: 'Us',
            data: decimatedUs,
            backgroundColor: '#7fe526',
            borderRadius: 6,
            maxBarThickness: 36,
            categoryPercentage: 0.35,
            barPercentage: 1.0,
          });
        }
        if (showSide !== 'us') {
          datasets.push({
            label: 'Them',
            data: decimatedThem,
            backgroundColor: '#255393',
            borderRadius: 6,
            maxBarThickness: 36,
            categoryPercentage: 0.35,
            barPercentage: 1.0,
          });
        }

        return {
          labels: decimatedLabels,
          datasets,
        };
      }

      const datasets = [];
      if (showSide !== 'them') {
        datasets.push({
          label: 'Us',
          data: statSeries.us,
          backgroundColor: '#7fe526',
          borderRadius: 6,
          maxBarThickness: 36,
          categoryPercentage: 0.35,
          barPercentage: 1.0,
        });
      }
      if (showSide !== 'us') {
        datasets.push({
          label: 'Them',
          data: statSeries.them,
          backgroundColor: '#255393',
          borderRadius: 6,
          maxBarThickness: 36,
          categoryPercentage: 0.35,
          barPercentage: 1.0,
        });
      }
      return {
        labels: statSeries.labels,
        datasets,
      };
    }
  }, [statSeries, showSide, decimateData]);

  // Stable callback functions to prevent chart re-renders
  const tooltipTitleCallback = useCallback(
    (items: any[]) => {
      if (!items.length) return '';
      const idx = items[0].dataIndex;
      return statSeries.fullOpponents[idx] || '';
    },
    [statSeries.fullOpponents],
  );

  const tooltipLabelCallback = useCallback(
    (item: any) => {
      const idx = item.dataIndex ?? 0;
      if (showSide === 'wins') {
        const side = statSeries.outcomes[idx] === 'win' ? 'Us' : statSeries.outcomes[idx] === 'loss' ? 'Them' : '';
        return side ? `${side}: ${item.formattedValue}` : `${item.formattedValue}`;
      }
      if (showSide === 'losses') {
        const side = statSeries.outcomes[idx] === 'win' ? 'Them' : statSeries.outcomes[idx] === 'loss' ? 'Us' : '';
        return side ? `${side}: ${item.formattedValue}` : `${item.formattedValue}`;
      }
      return `${item.dataset.label}: ${item.formattedValue}`;
    },
    [statSeries.outcomes, showSide],
  );

  const tickFontCallback = useCallback(
    (context: any) => {
      const index = context.index;
      const outcome = statSeries.outcomes[index];
      return {
        size: 11,
        // Bold wins and ties; normal for losses/others
        weight: outcome === 'win' || outcome === 'tie' ? ('bold' as const) : ('normal' as const),
        family: 'Arial, sans-serif',
      };
    },
    [statSeries.outcomes],
  );

  const tickColorCallback = useCallback(
    (context: any) => {
      const index = context.index;
      const outcome = statSeries.outcomes[index];
      return outcome === 'win' ? '#000000' : '#666666';
    },
    [statSeries.outcomes],
  );

  const tickLabelCallback = useCallback(function (this: any, value: any, index: number, ticks: any[]) {
    const label = this.getLabelForValue(value as any);
    return label;
  }, []);

  // Memoize chart options to prevent unnecessary re-renders
  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 2500, // Disable animations for better performance
      },
      plugins: {
        legend: { display: true, position: 'top' as const },
        title: { display: false },
        tooltip: {
          enabled: true,
          callbacks: {
            title: tooltipTitleCallback,
            label: tooltipLabelCallback,
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            maxRotation: 45,
            minRotation: 45,
            autoSkip: false,
            font: tickFontCallback,
            color: tickColorCallback,
            callback: tickLabelCallback,
          },
        },
        y: {
          beginAtZero: true,
          ticks: { font: { size: 12 } },
          grid: { color: '#e5e7eb' },
        },
      },
    }),
    [tooltipTitleCallback, tooltipLabelCallback, tickFontCallback, tickColorCallback, tickLabelCallback],
  );

  // Transform backend stat series data to frontend column format
  // calcPerGameColumn inlined in fetchAllPeriodsStats

  return (
    <div className="w-full mb-10 mt-6 px-4 sm:px-6 lg:px-8">
      {/* Unified responsive header */}
      <header className="flex justify-between gap-4 items-stretch w-full mb-2">
        {/* Left: header text and subtext */}
        <div className="w-[55%] sm:flex-1 min-w-0 flex flex-col justify-end">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-sd-navy">
              <span className="inline md:hidden">Team Report</span>
              <span className="hidden md:inline">Teams Analytics</span>
            </h1>
          </div>
        </div>
        {/* Right: dropdowns. All three on the same row */}
        <div className="w-[45%] md:w-auto shrink-0 flex flex-col gap-2 md:gap-2 items-stretch md:items-end justify-end">
          {/* All selects on one row */}
          <div className="flex flex-col md:flex-row gap-2 md:gap-3 w-full md:w-auto">
            <div className="relative w-full md:w-64" ref={gameDropdownRef}>
              <button
                type="button"
                className={`${TEAMS_FILTER_TRIGGER_CLASS} w-full text-left truncate`}
                onClick={() => setIsGameDropdownOpen((prev) => !prev)}
              >
                {selectedGameLabel}
              </button>
              <span className="pointer-events-none absolute right-2 top-1/2 transform -translate-y-1/2">
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                  <rect x="1" y="1" width="18" height="18" rx="4" stroke="rgb(var(--sd-navy))" strokeWidth="2" fill="rgb(var(--sd-navy))" />
                  <path d="M7 8l3 3 3-3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
              {isGameDropdownOpen && (
                <div className="absolute right-0 mt-1 z-50 w-full max-h-72 overflow-y-auto rounded-md border border-sd-line bg-white shadow-lg p-2">
                  <button
                    type="button"
                    className="block w-full rounded-md px-2 py-1.5 min-h-8 text-left text-sm font-semibold text-sd-navy hover:bg-sd-light"
                    onClick={() => selectGamePreset('all')}
                  >
                    All Games
                  </button>
                  {gamesMode !== GAME_MODE_PRACTICE && (
                    <button
                      type="button"
                      className="block w-full rounded-md px-2 py-1.5 min-h-8 text-left text-sm font-semibold text-sd-navy hover:bg-sd-light"
                      onClick={() => selectGamePreset('league')}
                    >
                      League Games
                    </button>
                  )}
                  <div className="my-1 border-t border-sd-line" />
                  {selectableGames.map((g) => {
                    const gid = String(g._id);
                    const isChecked = selectedCustomGameIds.includes(gid);
                    return (
                      <label
                        key={gid}
                        className="flex min-h-8 cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm font-semibold text-sd-navy hover:bg-sd-light"
                      >
                        <input type="checkbox" className="h-4 w-4 shrink-0" checked={isChecked} onChange={() => toggleSelectedGame(g)} />
                        <span className="truncate">{formatGameOptionLabel(g)}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="relative">
              <select
                className={`${TEAMS_FILTER_TRIGGER_CLASS} min-w-[100px]`}
                value={showSide}
                onChange={(e) => setShowSide(e.target.value as any)}
              >
                <option value="both">Both</option>
                <option value="us">Us</option>
                <option value="them">Them</option>
                <option value="wins">Wins</option>
                <option value="losses">Losses</option>
              </select>
              <span className="pointer-events-none absolute right-2 top-1/2 transform -translate-y-1/2">
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                  <rect x="1" y="1" width="18" height="18" rx="4" stroke="rgb(var(--sd-navy))" strokeWidth="2" fill="rgb(var(--sd-navy))" />
                  <path d="M7 8l3 3 3-3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </div>
            <div className="relative">
              <select
                className={`${TEAMS_FILTER_TRIGGER_CLASS} min-w-[130px]`}
                value={selectedStat}
                onChange={(e) => setSelectedStat(e.target.value)}
              >
                {statOptions.map((opt) => (
                  <option key={opt.label} value={opt.label}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <span className="pointer-events-none absolute right-2 top-1/2 transform -translate-y-1/2">
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                  <rect x="1" y="1" width="18" height="18" rx="4" stroke="rgb(var(--sd-navy))" strokeWidth="2" fill="rgb(var(--sd-navy))" />
                  <path d="M7 8l3 3 3-3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Single Stat Bar Chart (mobile scrollable) */}
      <div className="mb-8 md:border md:border-sd-line -mx-4 md:mx-0 md:p-4">
        <div className="flex flex-col gap-4 w-full p-0 shadow-[0_4px_8px_rgba(0,0,0,0.20),_0_-4px_8px_rgba(0,0,0,0.20)] md:p-4 md:bg-white">
          <div className="bg-white rounded-none py-6 md:rounded-none px-0 md:px-0 md:py-0 flex flex-col">
            <p className="text-[20px] font-bold text-sd-dark text-center">{selectedStat} by Game</p>
            <div className="relative flex-grow">
              {/* Left gradient and button (mobile only) */}
              {showChartLeft && (
                <div
                  aria-label="Scroll left"
                  onClick={() => scrollChart(-1)}
                  className="absolute top-0 bottom-0 left-0 w-8 flex items-center justify-start pl-1 md:hidden z-20 cursor-pointer select-none bg-gradient-to-r from-white via-white/70 md:from-sd-light md:via-sd-light/70 to-transparent"
                >
                  <span className="text-sd-navy text-5xl leading-none drop-shadow ml-1">‹</span>
                </div>
              )}
              {/* Right gradient and button (mobile only) */}
              {showChartRight && (
                <div
                  aria-label="Scroll right"
                  onClick={() => scrollChart(1)}
                  className="absolute top-0 bottom-0 right-0 w-8 flex items-center justify-end pr-1 md:hidden z-20 cursor-pointer select-none bg-gradient-to-l from-white via-white/70 md:from-sd-light md:via-sd-light/70 to-transparent"
                >
                  <span className="text-sd-navy text-5xl leading-none drop-shadow mr-1">›</span>
                </div>
              )}
              <div ref={chartScrollRef} className="h-full overflow-x-auto md:overflow-visible scrollbar-hide">
                <div className="h-96 min-w-[1200px] md:min-w-0">
                  <Bar data={chartData} options={chartOptions} plugins={[highlightCategoryPlugin]} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tables (always show all sections) */}
      <div className="md:mx-0 bg-transparent overflow-visible relative">
        {/* Loading Spinner */}
        {isLoading && (
          <div role="status" className="absolute -translate-x-1/2 -translate-y-1/2 top-2/4 left-1/2 z-50">
            <svg
              aria-hidden="true"
              className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C0 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <span className="sr-only">Loading...</span>
          </div>
        )}
        <div
          className={`flex flex-col items-center w-full p-0 md:p-0 ${
            isLoading ? 'opacity-20 pointer-events-none' : ''
          }`}
        >
          <div className="flex flex-col gap-6 md:gap-8 w-full px-0 md:px-0">
            <ReportSection title="Possession">
              <TeamsAnalyticsTable fields={getPossessionFields()} columns={columns} kpiEnabled={!!kpiTargets} />
            </ReportSection>
            <ReportSection title="Offense">
              <TeamsAnalyticsTable fields={offenseFields} columns={columns} kpiEnabled={!!kpiTargets} />
            </ReportSection>
            <ReportSection title="Defense">
              <TeamsAnalyticsTable fields={defenseFields} columns={columns} kpiEnabled={!!kpiTargets} />
            </ReportSection>
            <ReportSection title="Goalie">
              <TeamsAnalyticsTable fields={goalieFields} columns={columns} kpiEnabled={!!kpiTargets} />
            </ReportSection>
            <ReportSection title="Clears / Rides">
              <TeamsAnalyticsTable fields={clearsRidesFields} columns={columns} kpiEnabled={!!kpiTargets} />
            </ReportSection>
            <ReportSection title="Draw Controls">
              <TeamsAnalyticsTable fields={faceoffsFields} columns={columns} kpiEnabled={!!kpiTargets} />
            </ReportSection>
            <ReportSection title="8M Possessions">
              <TeamsAnalyticsTable fields={eightMeterFields} columns={columns} kpiEnabled={!!kpiTargets} />
            </ReportSection>
            <ReportSection title="Fouls">
              <TeamsAnalyticsTable fields={foulsFields} columns={columns} kpiEnabled={!!kpiTargets} />
            </ReportSection>
          </div>
        </div>
      </div>
    </div>
  );
});

export default SeasonTeamsPage;
